import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { AuthState, User, LoginCredentials, RegisterData } from '@/types';
import { authService } from '@/services/auth';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<{ success: boolean; error?: string; user?: User; requiresVerification?: boolean }>;
  register: (data: RegisterData) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  verifyEmail: (code: string) => Promise<{ success: boolean; error?: string }>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

type AuthAction = 
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'VERIFY_SUCCESS'; payload: User };

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  loading: true
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload
      };
    case 'VERIFY_SUCCESS':
      return {
        ...state,
        user: action.payload
      };
    default:
      return state;
  }
}

// Storage helper for web
const storage = {
  async getItem(key: string): Promise<string | null> {
    return localStorage.getItem(key);
  },
  
  async setItem(key: string, value: string): Promise<void> {
    localStorage.setItem(key, value);
  },
  
  async deleteItem(key: string): Promise<void> {
    localStorage.removeItem(key);
  }
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = await storage.getItem('authToken');
      if (token) {
        authService.setToken(token);
        const user = await authService.getProfile();
        dispatch({ type: 'LOGIN_SUCCESS', payload: { user, token } });
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      await storage.deleteItem('authToken');
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const login = async (credentials: LoginCredentials): Promise<{ success: boolean; error?: string; user?: User; requiresVerification?: boolean }> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await authService.login(credentials);
      
      if (response.success && response.data) {
        const { user, token } = response.data;
        
        // 如果用户未验证，返回特殊状态
        if (!user.isVerified) {
          return { 
            success: false, 
            requiresVerification: true, 
            user, 
            error: '账户尚未验证，请先验证邮箱' 
          };
        }
        
        // 用户已验证，正常登录
        await storage.setItem('authToken', token);
        authService.setToken(token);
        dispatch({ 
          type: 'LOGIN_SUCCESS', 
          payload: { user, token }
        });
        return { success: true, user };
      }
      return { success: false, error: response.error || '登录失败' };
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, error: '登录失败，请重试' };
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const register = async (data: RegisterData): Promise<{ success: boolean; error?: string }> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await authService.register(data);
      if (response.success) {
        return { success: true };
      }
      return { success: false, error: response.error || '注册失败' };
    } catch (error) {
      console.error('Registration failed:', error);
      return { success: false, error: '注册失败，请重试' };
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const logout = async () => {
    await storage.deleteItem('authToken');
    authService.setToken(null);
    dispatch({ type: 'LOGOUT' });
  };

  const verifyEmail = async (code: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const response = await authService.verifyEmail(code);
      if (response.success && response.data) {
        dispatch({ type: 'VERIFY_SUCCESS', payload: response.data });
        return { success: true };
      }
      return { success: false, error: response.error || '验证失败' };
    } catch (error) {
      console.error('Email verification failed:', error);
      return { success: false, error: '验证失败，请重试' };
    }
  };

  const refreshUser = async () => {
    try {
      if (state.token) {
        console.log('Refreshing user profile...');
        const response = await authService.getProfile();
        console.log('Profile response:', response);
        
        // authService.getProfile() returns the user data directly, not wrapped in ApiResponse
        if (response) {
          dispatch({ type: 'UPDATE_USER', payload: response });
          console.log('User profile updated successfully');
        } else {
          console.error('No user data in profile response');
        }
      } else {
        console.log('No token available for refresh');
      }
    } catch (error: any) {
      console.error('Failed to refresh user:', error);
      // If refresh fails due to invalid token, logout
      if (error?.status === 401) {
        await logout();
      }
    }
  };

  return (
    <AuthContext.Provider value={{
      ...state,
      login,
      register,
      logout,
      verifyEmail,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}